import { createSlice, current } from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { setCurrentUser } from "./currentuserslice";
import { notificationDurationMiddle } from "../../lib/utils";
import {
  canCreateConnection,
  canEditConnection,
  canDeleteConnection,
  canViewAnyConnection,
} from "../../routing/authorizationhandlers";

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  filter: "",
  showSystemEntities: true,
  items: [],
  openCreateConnectionPopup: false,
  openUpdateConnectionPopup: false,
  selectedConnector: null,
  loadingConnectors: false,
  connectors: [],
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canUpdateIsPublic: false,
  updatingConnection: {
    Id: "",
    ConnectionTitle: "",
    IsPublic: false,
  },
  panes: [
    {
      // size: "30%",
      size: "25%",
      min: "25%",
      max: "25%",
      collapsible: false,
    },
    {
      //min: "700px",
      //size:"60%",
      min: "75%",
      max: "75%",
      collapsible: false,
    },
  ],
};

const connectionLocatorPageSlice = createSlice({
  name: "connectionLocatorPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      //payload: {processing=true, processingText=false}, when processing is false, processingText is ""
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setOpenCreateConnectionPopup: (state, action) => ({
      ...state,
      openCreateConnectionPopup: action.payload,
    }),

    setConnectors: (state, action) => ({
      ...state,
      connectors: action.payload,
    }),

    setOpenUpdateConnectionPopup: (state, action) => ({
      ...state,
      openUpdateConnectionPopup: action.payload,
    }),

    setUpdatingConnection: (state, action) => ({
      ...state,
      updatingConnection: action.payload,
    }),

    setFormValue: (state, action) => {
      //payload: {id: fieldId, value: fieldValue}
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: { ...state.formValues, [id]: value },
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    pageLoaded: (state, action) => {
      //payload: {reports: , currentUser: ,... }
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        items: action.payload.connections,
        canView: canViewAnyConnection(action.payload.currentUser),
        canCreate: canCreateConnection(action.payload.currentUser),
        canEdit: canEditConnection(action.payload.currentUser),
        canDelete: canDeleteConnection(action.payload.currentUser),
        canUpdateIsPublic:
          action.payload.currentUser?.IsFederationRootUser === true,
        pageDisposed: false,
      };
    },

    entityDeleted: (state, action) => { 
      let pageState = current(state);

      if (pageState.pageDisposed) return state;
      let id = action.payload;
      let newItems = pageState.items.filter((e) => e.ConnectionId != id);

      return {
        ...state,
        items: newItems,
      };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default connectionLocatorPageSlice.reducer;

export const loadPage = (currentUser, t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage") })
  );

  try {
    var response = await api({
      url: "connector/getconnections",
      method: "post",
      data: { userId: currentUser.AppUserId },
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().connectionLocatorPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data; 
    //transform the response
    dispatch(
      pageLoaded({
        connections: data.Connections,
        currentUser: currentUser,
      })
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const getConnectors = (t) => async (dispatch, getState) => {
  try {
    const response = await api({
      url: "connector/getconnectors",
      method: "post",
    });

    if (getState().connectionLocatorPage.pageDisposed) return;

    const connectors = response?.data?.Data?.Connectors ?? [];

    dispatch(setConnectors(connectors));

    return Promise.resolve(response);
  } catch (err) {
    return Promise.reject(err);
  }
};

export const redirectToGoogleAuth =
  (connectorId, t) => async (dispatch, getState) => {
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("connection.redirecting"),
      })
    );

    try {
      const response = await api({
        url: "google/getgoogleauthurl",
        method: "post",
        data: { connectorId },
      });

      if (getState().connectionLocatorPage.pageDisposed) return;

      const redirectUrl = response?.data?.Data?.RedirectUrl;

      if (redirectUrl) {
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      }

      dispatch(setProcessing({ processing: false }));
      dispatch(setOpenCreateConnectionPopup(false));

      return Promise.resolve(response);
    } catch (err) {
      dispatch(setProcessing({ processing: false }));
      return Promise.reject(err);
    }
  };

export const updateConnection =
  (id, title, isPublic, t) => async (dispatch, getState) => { 
    //payload the entity referenceKey
    dispatch(setProcessing(true));

    try {
      var response = await api({
        url: "connector/updateconnection",
        method: "post",
        data: { Id: id, ConnectionTitle: title, IsPublic: isPublic },
      });

      dispatch(setProcessing({ processing: false }));

      dispatch(setOpenUpdateConnectionPopup(false));

      dispatch(loadPage(getState().currentUser, t));
      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const deleteEntity = (id, t) => async (dispatch, getState) => { 
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("connection.deletingConnection"),
    })
  );

  try {
    var response = await api({
      url: "connector/cancelconnectionbyid",
      method: "post",
      data: { Id: id },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().connectionLocatorPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(setProcessing({ processing: false }));

    dispatch(entityDeleted(id));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  setPanes,
  pageLoaded,
  setFormValue,
  entityDeleted,
  disposePage,
  setOpenCreateConnectionPopup,
  setConnectors,
  setOpenUpdateConnectionPopup,
  setUpdatingConnection,
} = connectionLocatorPageSlice.actions;
